import { Link } from "react-router-dom";
import { useMemo } from "react";
import { motion } from "motion/react";
import {
  ArrowRight, Sparkles, GitFork, Scale, Network, MessageSquare,
  Activity, ShieldQuestion, RefreshCw, Layers, ChevronRight,
} from "lucide-react";
import { Button } from "@/components/Button";
import { Badge, TagChip } from "@/components/Badge";
import { StrainCard } from "@/components/StrainCard";
import { useStore } from "@/store/useStore";
import { Logo } from "@/components/AppShell";

export default function Landing() {
  const loginDemo = useStore((s) => s.loginDemo);
  const strains = useStore((s) => s.strains);
  const sample = useMemo(() => strains.slice(0, 3), [strains]);

  return (
    <div className="min-h-screen bg-strain-field">
      {/* nav */}
      <nav className="sticky top-0 z-40 glass border-b border-[var(--color-border)]">
        <div className="max-w-6xl mx-auto h-14 px-4 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2">
            <Logo className="w-7 h-7" />
            <span className="text-sm font-semibold tracking-tight">idea<span className="text-[var(--color-primary)]">strains</span></span>
          </Link>
          <div className="flex-1" />
          <Link to="/about" className="text-sm text-[var(--color-ink-muted)] hover:text-[var(--color-ink)] px-2 h-9 inline-flex items-center rounded-lg hover:bg-[var(--color-surface-2)] transition-colors">
            How it works
          </Link>
          <Link to="/app" className="text-sm text-[var(--color-ink-muted)] hover:text-[var(--color-ink)] px-2 h-9 inline-flex items-center rounded-lg hover:bg-[var(--color-surface-2)] transition-colors">
            Explore
          </Link>
          <Link to="/app" onClick={() => loginDemo().catch(() => undefined)} className="text-sm font-semibold h-9 px-3 inline-flex items-center rounded-lg bg-[var(--color-primary)] text-[var(--color-void)] hover:bg-[var(--color-primary-bright)] transition-colors">
            Get started
          </Link>
        </div>
      </nav>

      {/* hero */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[var(--color-base)]" />
        <div className="relative max-w-6xl mx-auto px-4 pt-16 pb-20 grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] text-[11px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)] mb-6"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--color-primary)] animate-pulse" />
              a new kind of social app for ideas
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="text-5xl sm:text-6xl font-semibold tracking-tight leading-[1.02]"
            >
              Ideas that <span className="text-gradient-primary">fork, fight,</span>
              <br />merge, and mutate.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mt-5 text-lg text-[var(--color-ink-soft)] max-w-xl leading-relaxed"
            >
              Publish an idea as an interactive, forkable map. Find its weak points.
              Challenge the inferences, not just the claims. Watch minds actually move.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="mt-7 flex flex-wrap items-center gap-3"
            >
              <Link to="/create" onClick={() => loginDemo().catch(() => undefined)}>
                <Button size="lg" rightIcon={<ArrowRight size={17} />}>Create a strain</Button>
              </Link>
              <Link to="/app">
                <Button
                  size="lg"
                  variant="outline"
                  leftIcon={<Sparkles size={16} />}
                  className="!bg-[var(--color-surface)] !text-[var(--color-ink)] shadow-card hover:!bg-[var(--color-surface-2)] hover:!text-[var(--color-primary-bright)]"
                >
                  Explore examples
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="mt-6 flex items-center gap-4 text-[12px] text-[var(--color-ink-faint)]"
            >
              <span className="inline-flex items-center gap-1"><Scale size={12} /> find the crux</span>
              <span className="inline-flex items-center gap-1"><GitFork size={12} /> fork what changes</span>
              <span className="inline-flex items-center gap-1"><Activity size={12} /> measure belief change</span>
            </motion.div>
          </div>

          {/* hero live graph */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 24 }}
            className="relative"
          >
            <HeroGraph />
          </motion.div>
        </div>
      </header>

      {/* three pillars */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-3 gap-4">
          <Pillar
            icon={<Network size={20} />}
            tone="primary"
            title="Map the idea."
            desc="Turn messy prose into a forkable structure of claims, evidence, hidden warrants, and cruxes — with your original wording preserved."
          />
          <Pillar
            icon={<Scale size={20} />}
            tone="amber"
            title="Find the crux."
            desc="The app surfaces load-bearing points where the idea could change. Not a truth machine — a hinge-finder."
          />
          <Pillar
            icon={<GitFork size={20} />}
            tone="inferred"
            title="Fork what changes."
            desc="Disagree precisely. Fork a node, an inference, or the whole strain. See exactly what changed, and whose minds moved."
          />
        </div>
      </section>

      {/* not a normal debate app */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        <div className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-surface)] p-8 sm:p-12 relative overflow-hidden">
          <div className="absolute -right-20 -top-20 w-64 h-64 rounded-full bg-[rgba(167,139,250,0.08)] blur-3xl" />
          <div className="relative">
            <Badge tone="inferred" icon={<ShieldQuestion size={11} />}>not a normal debate app</Badge>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight max-w-2xl leading-tight">
              The durable object is the map, not the fight.
            </h2>
            <p className="mt-4 text-[var(--color-ink-soft)] max-w-2xl leading-relaxed">
              Normal online debate creates disposable comments. Idea Strains creates durable artifacts:
              structures you can inspect, extend, challenge, repair, and merge. No raw likes. No dunking.
              Just the structure of thinking, made visible.
            </p>
            <div className="mt-6 grid sm:grid-cols-2 gap-2 max-w-2xl">
              {[
                "Undercuts attack the inference between two claims — not just the claims themselves.",
                "AI suggestions are always labeled inferred until you accept them. Never silent verdicts.",
                "Stance sliders measure where minds move, not who 'won'.",
                "Bridge proposals are first-class — reformulations both camps might accept.",
              ].map((t) => (
                <div key={t} className="flex items-start gap-2 text-[13px] text-[var(--color-ink-soft)]">
                  <ChevronRight size={14} className="text-[var(--color-primary)] mt-0.5 shrink-0" />
                  {t}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* how it works strip */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        <h2 className="text-2xl font-semibold tracking-tight mb-6">How it works</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { n: "1", icon: <MessageSquare size={16} />, t: "Paste an idea", d: "Write or paste normal prose. No structure required." },
            { n: "2", icon: <Sparkles size={16} />, t: "Draft a map", d: "AI proposes claims, evidence, hidden warrants, and likely cruxes." },
            { n: "3", icon: <Scale size={16} />, t: "Find the crux", d: "See where the idea hinges. Accept, reject, or fork each point." },
            { n: "4", icon: <GitFork size={16} />, t: "Fork & evolve", d: "Disagree precisely. Watch versions branch and minds move." },
          ].map((s) => (
            <div key={s.n} className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
              <div className="flex items-center gap-2 mb-3">
                <span className="font-mono text-[11px] text-[var(--color-ink-faint)]">{s.n}</span>
                <span className="text-[var(--color-primary)]">{s.icon}</span>
              </div>
              <div className="text-[14px] font-semibold mb-1">{s.t}</div>
              <div className="text-[12px] text-[var(--color-ink-muted)] leading-relaxed">{s.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* example strains */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight">Example strains</h2>
            <p className="text-[13px] text-[var(--color-ink-muted)] mt-1">Real ideas, mapped. Click in and explore the canvas, spine, crux, and lineage views.</p>
          </div>
          <Link to="/app" className="text-sm text-[var(--color-primary)] hover:underline inline-flex items-center gap-1">
            See all <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sample.map((s, i) => <StrainCard key={s.id} strain={s} index={i} />)}
        </div>
      </section>

      {/* footer */}
      <footer className="border-t border-[var(--color-border)] mt-12">
        <div className="max-w-6xl mx-auto px-4 py-8 flex flex-col sm:flex-row items-center gap-3">
          <div className="flex items-center gap-2">
            <Logo className="w-5 h-5" />
            <span className="text-sm font-semibold">ideastrains.com</span>
          </div>
          <div className="flex items-center gap-4 text-[12px] text-[var(--color-ink-muted)] sm:ml-auto">
            <Link to="/about" className="hover:text-[var(--color-ink)]">About</Link>
            <Link to="/app" className="hover:text-[var(--color-ink)]">Explore</Link>
            <Link to="/create" className="hover:text-[var(--color-ink)]">Create</Link>
          </div>
          <div className="text-[11px] text-[var(--color-ink-faint)] font-mono">prototype · ai by gpt-5.5</div>
        </div>
      </footer>
    </div>
  );
}

function Pillar({ icon, tone, title, desc }: { icon: React.ReactNode; tone: string; title: string; desc: string }) {
  const color = tone === "primary" ? "var(--color-primary)" : tone === "amber" ? "var(--color-secondary)" : "var(--color-inferred)";
  return (
    <motion.div
      whileHover={{ y: -3 }}
      className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6"
    >
      <div className="w-11 h-11 rounded-xl grid place-items-center mb-4" style={{ background: `color-mix(in srgb, ${color} 14%, transparent)`, color }}>
        {icon}
      </div>
      <h3 className="text-lg font-semibold mb-1.5">{title}</h3>
      <p className="text-[13px] text-[var(--color-ink-muted)] leading-relaxed">{desc}</p>
    </motion.div>
  );
}

// a self-contained animated mini-graph for the hero
function HeroGraph() {
  const nodes = [
    { id: "h1", x: 30, y: 150, r: 8, color: "var(--color-primary)", label: "thesis" },
    { id: "h2", x: 150, y: 60, r: 5, color: "var(--color-support)", label: "support" },
    { id: "h3", x: 150, y: 240, r: 5, color: "var(--color-rebut)", label: "rebuttal" },
    { id: "h4", x: 270, y: 150, r: 6, color: "var(--color-undercut)", label: "undercut" },
    { id: "h5", x: 270, y: 300, r: 4, color: "var(--color-evidence)", label: "evidence" },
  ];
  const edges = [
    { a: 0, b: 1, color: "var(--color-support)", dash: "" },
    { a: 0, b: 2, color: "var(--color-rebut)", dash: "" },
    { a: 3, b: 0, color: "var(--color-undercut)", dash: "5 4" },
    { a: 4, b: 2, color: "var(--color-evidence)", dash: "2 4" },
  ];

  return (
    <div className="relative rounded-3xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6 shadow-card overflow-hidden">
      <div className="absolute inset-0 bg-grid-fine opacity-50" />
      <div className="relative">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[var(--color-primary)]" />
            <span className="text-[11px] font-mono text-[var(--color-ink-muted)] uppercase tracking-wider">college worth it?</span>
          </div>
          <Badge tone="amber" icon={<Scale size={9} />}>crux: selection effect</Badge>
        </div>

        <svg viewBox="0 0 320 340" className="w-full h-auto">
          <defs>
            <marker id="hero-arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
              <path d="M0,0 L6,3 L0,6 Z" fill="var(--color-ink-muted)" />
            </marker>
          </defs>
          {edges.map((e, i) => {
            const a = nodes[e.a], b = nodes[e.b];
            return (
              <motion.path
                key={i}
                d={`M${a.x},${a.y} Q${(a.x + b.x) / 2},${(a.y + b.y) / 2 - 15} ${b.x},${b.y}`}
                stroke={e.color}
                strokeWidth="1.5"
                fill="none"
                strokeDasharray={e.dash}
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 0.7 }}
                transition={{ delay: 0.5 + i * 0.15, duration: 0.8 }}
              />
            );
          })}
          {nodes.map((n, i) => (
            <motion.g
              key={n.id}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3 + i * 0.12, type: "spring", stiffness: 260, damping: 18 }}
              style={{ transformOrigin: `${n.x}px ${n.y}px` }}
            >
              {i === 0 && (
                <circle cx={n.x} cy={n.y} r={n.r + 6} fill="none" stroke={n.color} strokeWidth="1" opacity="0.3">
                  <animate attributeName="r" values={`${n.r + 6};${n.r + 10};${n.r + 6}`} dur="3s" repeatCount="indefinite" />
                </circle>
              )}
              <circle cx={n.x} cy={n.y} r={n.r} fill="var(--color-surface)" stroke={n.color} strokeWidth="2" />
              <circle cx={n.x} cy={n.y} r={n.r - 3} fill={n.color} />
              {i === 3 && (
                <circle cx={n.x} cy={n.y} r="3" fill="var(--color-undercut)">
                  <animate attributeName="opacity" values="1;0.4;1" dur="2s" repeatCount="indefinite" />
                </circle>
              )}
            </motion.g>
          ))}
        </svg>

        <div className="grid grid-cols-2 gap-2 mt-4">
          <Legend color="var(--color-support)" label="supports" dash="solid" />
          <Legend color="var(--color-rebut)" label="rebuts" dash="solid" />
          <Legend color="var(--color-undercut)" label="undercuts" dash="dashed" />
          <Legend color="var(--color-evidence)" label="evidences" dash="dotted" />
        </div>
      </div>
    </div>
  );
}

function Legend({ color, label, dash }: { color: string; label: string; dash: string }) {
  return (
    <div className="flex items-center gap-2 text-[11px] font-mono text-[var(--color-ink-muted)]">
      <svg width="24" height="6">
        <line x1="0" y1="3" x2="24" y2="3" stroke={color} strokeWidth="2" strokeDasharray={dash === "dashed" ? "4 3" : dash === "dotted" ? "1.5 3" : undefined} />
      </svg>
      {label}
    </div>
  );
}
